/* oxlint-disable react/refs -- imperative refs are required for DOM, focus, drag, and media behavior */
import { observer } from 'mobx-react-lite'
import {
  ArrowPathIcon,
  CalendarIcon,
  ChatBubbleLeftRightIcon,
  EllipsisHorizontalCircleIcon,
  InboxStackIcon,
} from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { compact, uniq } from 'lodash'
import { ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery } from 'urql'
import { useFeatureToggle } from '../../hooks/useFeatureToggle'
import Spinner from '../Spinner'
import ActivityPanel from 'app/components/contacts/ActivityPanel'
import { TransactionTypeIcon } from 'app/components/activities/TransactionTypeIcon'
import { FilterChip, FilterChips } from 'app/components/contacts/FilterChips'
import { useCommLog, useCommLogCount } from 'app/components/contacts/commlog/useCommLog'
import { useTreatmentWork } from 'app/components/contacts/treatment/useTreatmentWork'
import { DocumentsTab } from 'app/components/paperwork/DocumentsTab'
import { useContactDocuments } from 'app/components/paperwork/useContactDocuments'
import {
  ACTIVITY_ACTIVITIES,
  ACTIVITY_KIND_LABELS,
  ACTIVITY_KINDS,
  ACTIVITY_OTHER,
  ACTIVITY_UPDATES,
  ActivityKind,
  countActivitiesByKind,
  DEFAULT_ACTIVITY_KINDS,
  filterActivitiesByKinds,
  kindsForActivity,
} from 'app/lib/activityKinds'
import { toggleChipSelection } from 'app/lib/chipFilters'
import { PMS_APPOINTMENTS, PMS_COMM_LOG, PMS_RECORD_KINDS, PmsRecordKind, RECORD_ACTIVITIES } from 'app/lib/pmsRecords'
import { usePersistedState } from 'app/hooks/usePersistedState'
import {
  HAS_ACTIVE_AGENT_QUERY,
  THREAD_QUERY,
  UPDATE_THREAD_MUTATION,
  type HasActiveAgentResult,
} from 'app/components/inbox/graphql'
import InlineTabs, { TabType } from 'app/components/InlineTabs'
import type { ActivityContact, ActivityJson } from 'app/types/entities'
import type { AppointmentJson } from 'app/types/entities'
import type { ContactJson } from 'app/types/entities'
import type { LocationJson } from 'app/types/entities'
import type { MessageJson } from 'app/types/entities'
import type { TaskJson } from 'app/types/entities'
import type { TransactionJson } from 'app/types/entities'
import type { PlanWithLines } from 'app/types/treatment'
import { MARK_MESSAGE_READ_MUTATION } from 'app/graphql/messageMutations'
import { HELD_MESSAGES_QUERY } from 'app/graphql/scheduledMessages'
import { gqlMutation, gqlQuery } from 'app/lib/graphqlClient'
import { OPS_URL } from 'app/lib/opsUrqlClient'
import { useMessageEvents } from 'app/hooks/useMessageEvents'
import { notifyError } from 'app/services/notify.client'
import { useMessageStore, useAuthStore } from 'app/stores/StoreContext'
import { useUser, useWorkspace } from 'app/utils'
import { bg } from 'lib/background'
import { debug, logError } from 'lib/logging'
import { Jsonify } from 'lib/toJSON'
import { PMS_SUPPORT, pmsGate } from 'lib/pmsCapabilities'

/**
 * The Timeline's chip order, which is deliberately not the activity groups'
 * own order.
 *
 * What a person came to read leads: what happened with this patient, then when
 * they are booked. The PMS log is context behind that. Updates ends the row
 * because it is system bookkeeping, off by default, and the least likely thing
 * anyone is looking for.
 *
 * Kept as an explicit list rather than by reordering ACTIVITY_KINDS so the two
 * stay independent: that constant still means "activity groups, in order",
 * which the filter logic and its tests rely on. Anything absent here lands at
 * the end rather than disappearing, so a new group cannot go missing silently.
 */
const CHIP_ICON = `h-3.5 w-3.5 shrink-0`

/**
 * Tuned so the header's bottom border lands on the centre of the gutter between
 * the left pane's section cards rather than on one card's top edge. The two
 * panes are independent columns, so nothing enforces this; it is 14px because
 * that is what puts the line at the midpoint of the 4px gap.
 */
const CHIP_ROW_PADDING = `pt-3 pb-3.5`

/**
 * Chip glyphs, drawn as line art rather than the cards' filled tiles: a chip is
 * a control, and matching the cards' weight would have the filter row competing
 * with the feed it filters.
 */
const TIMELINE_CHIP_ICONS: Record<string, ReactNode> = {
  [ACTIVITY_ACTIVITIES]: <ChatBubbleLeftRightIcon className={CHIP_ICON} />,
  [PMS_APPOINTMENTS]: <CalendarIcon className={CHIP_ICON} />,
  [PMS_COMM_LOG]: <InboxStackIcon className={CHIP_ICON} />,
  [ACTIVITY_OTHER]: <EllipsisHorizontalCircleIcon className={CHIP_ICON} />,
  [ACTIVITY_UPDATES]: <ArrowPathIcon className={CHIP_ICON} />,
}

/**
 * The sources the Timeline opens with. The comm log is absent deliberately: it
 * is the largest and noisiest source, and opting into it is a decision.
 */
const DEFAULT_TIMELINE_KINDS: PmsRecordKind[] = [RECORD_ACTIVITIES, PMS_APPOINTMENTS]

/** The Ledger opens showing every transaction type the practice uses. */
const EMPTY_LEDGER_TYPES: string[] = []

const TIMELINE_CHIP_ORDER: string[] = [
  ACTIVITY_ACTIVITIES,
  PMS_APPOINTMENTS,
  PMS_COMM_LOG,
  ACTIVITY_OTHER,
  ACTIVITY_UPDATES,
]

const timelineChipRank = (kind: string) => {
  const index = TIMELINE_CHIP_ORDER.indexOf(kind)
  return index === -1 ? TIMELINE_CHIP_ORDER.length : index
}

/**
 * The panes the drawer's right side can show.
 *
 * Appointments and Transactions used to be tabs of their own. Appointments
 * folded into ALL, which is one stream with the sources as chips, because a
 * broken appointment and the text that cancelled it belong next to each other
 * and no single-select tab can show that. Transactions became LEDGER: the same
 * records, grouped by day with running totals, since money reads as an account
 * rather than as a story.
 */
export const enum ActivitySections {
  /** The story: activities, appointments and communications in one stream. */
  ALL = `all`,
  INBOX = `inbox`,
  /** Money, which reads as a running account rather than a story. */
  LEDGER = `ledger`,
  TREATMENT_PLANS = `treatment-plans`,
  /** Paperwork sent to the contact's patients, and what came back. */
  DOCUMENTS = `documents`,
}

/**
 * The Inbox tab's id as a plain value.
 *
 * `ActivitySections` is a `const enum` and this project sets
 * `isolatedModules`, so esbuild cannot inline it across a module boundary:
 * importing the enum elsewhere yields `undefined` at runtime while `tsc` still
 * type-checks clean. Anything outside this file must use this constant.
 */
export const INBOX_TAB: string = ActivitySections.INBOX
/** The Treatment Plans tab's id as a plain value, for the same reason. */
export const TREATMENT_PLANS_TAB: string = ActivitySections.TREATMENT_PLANS

// On mobile the drawer collapses the left-hand contact info into a leading tab
// alongside the related-object tabs, so everything lives in one tab strip.
const DETAILS_TAB = `details`

/**
 * Appointments, transactions, treatment plans and comm logs are all read-only
 * mirrors of the practice management system, and every new PMS entity we import
 * used to cost another top-level tab until the strip no longer fit. They live
 * behind one PMS tab now, as chips over a single merged timeline.
 */

// observer: reads a feature toggle (and possibly authStore.user) in render; useFeatureToggle resolves async and its contract requires an observer wrapper (TRK-6020).
export const ContactRelatedObjects = observer(function ContactRelatedObjects({
  contact,
  location,
  activities: activitiesFromProps,
  appointments: appointmentsFromProps,
  transactions: transactionsFromProps,
  treatmentPlans: treatmentPlansFromProps,
  totals: totalsFromProps,
  highlightActivityId,
  onChange,
  onRemovedTask,
  onTaskUpdated,
  onActivitiesChange,
  onAIThreadDetected,
  detailsTab,
  initialTab,
}: {
  contact?: ContactJson
  location?: LocationJson
  activities?: ActivityJson[]
  appointments?: AppointmentJson[]
  transactions?: TransactionJson[]
  treatmentPlans?: PlanWithLines[]
  totals?: {
    all?: number
    tasks?: number
    appointments?: number
    transactions?: number
    treatmentPlans?: number
  }
  highlightActivityId?: string
  onChange?: (contact: ContactJson) => void
  onRemovedTask?: () => void
  onTaskUpdated?: (task: TaskJson) => void
  onActivitiesChange?: (activities: ActivityJson[]) => void
  onAIThreadDetected?: (isAI: boolean) => void
  detailsTab?: { name: string; content: React.ReactNode }
  /**
   * Which tab to land on. Opening a contact from a text in the response queue
   * should put you in the conversation, not on the activity list you would then
   * have to navigate out of.
   */
  initialTab?: string
  /** Inbox is a mode reached from the header, not a tab. It takes the pane. */
}) {
  const me = useUser()
  const workspace = useWorkspace()
  const { products } = useAuthStore()
  const hasInbox = !!products?.includes(`Inbox`)
  const messageStore = useMessageStore()
  const [tab, setTab] = useState<string>(detailsTab ? DETAILS_TAB : ActivitySections.ALL)
  /**
   * Whether the feed below has moved off its top. Lives here rather than in the
   * panel because the header is the panel's sibling: both the header's shape
   * and the panel's own shadow answer to it.
   */
  const [feedScrolled, setFeedScrolled] = useState(false)
  // The chip selections outlive the drawer. Staff work a queue of contacts, and
  // re-hiding the same sources on every one of them is the app forgetting a
  // decision the user already made.
  const [timelineKinds, setTimelineKinds] = usePersistedState<PmsRecordKind[]>(
    `timeline-pms-kinds`,
    DEFAULT_TIMELINE_KINDS,
    (value): value is PmsRecordKind[] =>
      Array.isArray(value) && value.every((kind) => PMS_RECORD_KINDS.includes(kind as PmsRecordKind)),
  )
  // Ledger filters by the practice's own transaction vocabulary, which arrives
  // with the data. Tracking what is hidden rather than what is shown means a
  // type we have never seen before still appears the first time it turns up.
  //
  // Keyed by location because that vocabulary is the practice's, so one office's
  // hidden types mean nothing at the next.
  const [hiddenLedgerTypes, setHiddenLedgerTypes] = usePersistedState<string[]>(
    `ledger-hidden-types:${location?.id ?? `none`}`,
    EMPTY_LEDGER_TYPES,
    (value): value is string[] => Array.isArray(value) && value.every((type) => typeof type === `string`),
  )
  // Activities filters the same way the PMS stream does: additive chips over
  // one merged feed, rather than the dropdown that used to keep its selection
  // in the URL, in its own state and in the panel all at once.
  const [activityKinds, setActivityKinds] = usePersistedState<ActivityKind[]>(
    `timeline-activity-kinds`,
    DEFAULT_ACTIVITY_KINDS,
    (value): value is ActivityKind[] =>
      Array.isArray(value) && value.every((kind) => ACTIVITY_KINDS.includes(kind as ActivityKind)),
  )

  // Use data from props (parent component loads these now)
  const activities = activitiesFromProps
  // Never hide something the user just created. When an activity arrives whose
  // group is switched off, switch it on. Tracking ids rather than reacting to a
  // callback covers every creation path, not just the note modal that used to
  // nudge its type into the URL.
  const seenActivityIds = useRef<Set<string>>(undefined)
  useEffect(() => {
    if (!activities) return
    const ids = new Set(activities.map(({ id }) => id!).filter(Boolean))
    const previous = seenActivityIds.current
    seenActivityIds.current = ids
    if (!previous) return

    const revealed = activities
      .filter(({ id }) => id && !previous.has(id))
      .flatMap((activity) => kindsForActivity(activity))
    if (revealed.length === 0) return

    setActivityKinds((current) => {
      const missing = revealed.filter((kind) => !current.includes(kind))
      return missing.length === 0 ? current : [...current, ...new Set(missing)]
    })
  }, [activities])
  const appointments = appointmentsFromProps
  const transactions = transactionsFromProps
  const treatmentPlans = treatmentPlansFromProps

  const [isUpdatingMessages, setIsUpdatingMessages] = useState<boolean>(false)
  // A filter chip, not a destination, so it is absent rather than padlocked even with no PMS.
  const hasCommLog = !!location && PMS_SUPPORT.commLog(location)
  const commLogPatientIds = contact?.patientIds ?? []
  const commLogCount = useCommLogCount({ patientIds: commLogPatientIds, enabled: hasCommLog })
  const commLogFeed = useCommLog({
    patientIds: commLogPatientIds,
    active: tab === ActivitySections.ALL && timelineKinds.includes(PMS_COMM_LOG),
  })

  const isUpdatingMessagesRef = useRef(isUpdatingMessages)
  const latestLoadRequestIdRef = useRef(0)
  // One poll fetch in flight at a time. The shared clock ticks and pokes no
  // matter how slow the last request was; latestLoadRequestIdRef already discards
  // the stale response, this skips the wasted duplicate fetch. Only the tick path
  // is gated — mount and contact switches must always load.
  const pollFetchInFlightRef = useRef(false)
  isUpdatingMessagesRef.current = isUpdatingMessages

  const [messages, setMessages] = useState<MessageJson[]>([])
  /** Thread id for a conversation whose every row is held; see `loadMessages`. */
  const [heldThreadId, setHeldThreadId] = useState<string>()
  const [inboxContacts, setInboxContacts] = useState<Jsonify<ActivityContact>[]>([])
  const [messagesCount, setMessagesCount] = useState<number>(0)
  // Matches the main inbox's definition of unread: only customer-incoming messages
  // count. Excluding isAgent here is what keeps the tab pulse in sync with the
  // Mark Read action, which deliberately doesn't touch agent-sent messages.
  const hasNotification = messages.some((message) => !message.read && message.sentByUserId == null && !message.isAgent)

  // Use totals from props, but merge in messages count (which is still loaded locally)
  const totals = {
    ...(totalsFromProps ?? { all: 0, tasks: 0, appointments: 0, transactions: 0, treatmentPlans: 0 }),
    messages: messagesCount,
  }
  /**
   * What the Timeline's chips account for in the view it opens in.
   *
   * Only groups that are on by default.
   *
   * Counted from the same arrays the feed renders rather than a separate total,
   * which is what let the old number drift. It deliberately does not track live
   * chip state: a badge that moved on every toggle would restate what the chips
   * already say, louder.
   */
  const defaultTimelineCount =
    filterActivitiesByKinds(activities ?? [], DEFAULT_ACTIVITY_KINDS).length +
    (DEFAULT_TIMELINE_KINDS.includes(PMS_APPOINTMENTS) ? (appointments?.length ?? 0) : 0)

  /*
   * Loaded with the drawer rather than lazily on the tab, so the tab's own
   * badge counts the rows the tab will show. A count from anywhere else is a
   * number that can disagree with the list under it, which is exactly what the
   * old badge did: it counted legacy plans and read 0 forever.
   *
   * Cheap enough to be eager: only PENDING procedures are asked for, which
   * across this practice averages under two per patient.
   */
  const treatmentWork = useTreatmentWork({ patientIds: compact(contact?.patientIds), appointments })
  const treatmentGate = pmsGate(location ? [location] : [], `unscheduledTreatment`)
  const paperworkEnabled = useFeatureToggle(`paperwork`)
  // An empty list pauses the query, so contacts in workspaces without paperwork cost nothing.
  const documents = useContactDocuments(paperworkEnabled ? compact(contact?.patientIds) : [])

  const tabs: TabType[] = compact([
    detailsTab && {
      name: detailsTab.name,
      tab: DETAILS_TAB,
    },
    {
      name: `Timeline`,
      totalItems: String(defaultTimelineCount),
      tab: ActivitySections.ALL,
    },
    hasInbox && {
      name: `Inbox`,
      totalItems: String(totals.messages),
      tab: ActivitySections.INBOX,
      // Unread rides the tab badge rather than a separate dot: the count and
      // the "something is waiting" signal are the same fact.
      hasNotification,
    },
    {
      name: `Ledger`,
      totalItems: String(totals.transactions),
      tab: ActivitySections.LEDGER,
      isLocked: !location?.pmsIntegrated,
    },
    treatmentGate !== `hidden` && {
      name: `Treatment Plans`,
      totalItems: String(treatmentWork.procedures.length),
      tab: ActivitySections.TREATMENT_PLANS,
      isLocked: treatmentGate === `padlock`,
    },
    paperworkEnabled && {
      name: `Documents`,
      totalItems: String(documents.records.length),
      tab: ActivitySections.DOCUMENTS,
    },
  ])

  const ledgerTypes = uniq(compact((transactions ?? []).map((transaction) => transaction.transactionType))).sort()
  const visibleLedgerTypes = ledgerTypes.filter((type) => !hiddenLedgerTypes.includes(type))
  const ledgerChips: FilterChip<string>[] = ledgerTypes.map((type) => ({
    name: type,
    kind: type,
    totalItems: String((transactions ?? []).filter((transaction) => transaction.transactionType === type).length),
    icon: <TransactionTypeIcon type={type} className={`${CHIP_ICON} rounded p-0.5 text-on-color`} />,
  }))
  const visibleTransactions = (transactions ?? []).filter(
    (transaction) => !!transaction.transactionType && visibleLedgerTypes.includes(transaction.transactionType),
  )

  const activityCounts = countActivitiesByKind(activities ?? [])
  const activityChips: FilterChip<ActivityKind>[] = ACTIVITY_KINDS
    // "Other" only earns a chip once something actually falls into it, so the
    // row does not carry a permanently empty catch-all.
    .filter((kind) => kind !== ACTIVITY_OTHER || activityCounts[ACTIVITY_OTHER] > 0)
    .map((kind) => ({
      name: ACTIVITY_KIND_LABELS[kind],
      kind,
      totalItems: String(activityCounts[kind]),
      icon: TIMELINE_CHIP_ICONS[kind],
    }))

  // One chip row for the Timeline, mixing Trackable's activity groups with the
  // PMS sources that belong in the story, ordered by TIMELINE_CHIP_ORDER.
  const timelineChips: FilterChip<string>[] = compact([
    ...activityChips,
    {
      name: `Appointments`,
      kind: PMS_APPOINTMENTS,
      totalItems: String(totals.appointments),
      icon: TIMELINE_CHIP_ICONS[PMS_APPOINTMENTS],
    },
    hasCommLog && {
      name: `PMS Comm Log`,
      kind: PMS_COMM_LOG,
      totalItems: String(commLogCount),
      icon: TIMELINE_CHIP_ICONS[PMS_COMM_LOG],
    },
  ]).sort((a, b) => timelineChipRank(a.kind) - timelineChipRank(b.kind))

  /*
   * A stored selection can light no chip at all: the activities layer is
   * selected without having one, and Other only has one when something falls
   * into it. The feed is then empty under a row of dark chips, which reads as
   * the contact's history having gone missing. A selection like that loses to
   * the defaults, the way an unrecognized one does.
   *
   * Judged once activities have loaded, since that is when Other's chip is
   * known.
   */
  const storedKinds: string[] = [...activityKinds, ...timelineKinds]
  const hasLitChip = !activities || timelineChips.some((chip) => storedKinds.includes(chip.kind))
  const shownActivityKinds = hasLitChip ? activityKinds : DEFAULT_ACTIVITY_KINDS
  const shownTimelineKinds = hasLitChip ? timelineKinds : DEFAULT_TIMELINE_KINDS
  const shownKinds: string[] = [...shownActivityKinds, ...shownTimelineKinds]
  const litChips = timelineChips.filter((chip) => shownKinds.includes(chip.kind))

  const loadMessages = useCallback(
    async ({ force = false }: { force?: boolean } = {}) => {
      if (!contact?.id || !contact.locationId) return
      if (!force && isUpdatingMessagesRef.current) return
      const requestId = latestLoadRequestIdRef.current + 1
      latestLoadRequestIdRef.current = requestId

      // Match by explicit link OR by the contact's phone: inbound texts from a
      // number that was never linked to a contact (or was linked to another
      // contact sharing the number) still belong in this contact's Inbox tab.
      // The message index matches bare last-10-digit keys on from/to only
      // (see buildOptedOutSearches in useInboxController).
      const phoneKey = (contact.cellPhone ?? ``).replace(/\D/g, ``).slice(-10)
      const matchesContact = phoneKey
        ? `(contactId:${contact.id} OR from:"${phoneKey}" OR to:"${phoneKey}")`
        : `contactId:${contact.id}`
      const conversationSearch = `${matchesContact} AND locationId:${contact.locationId}`
      // network-only: this powers the drawer's message poll. The default
      // cache-first would replay the cached document on every tick, hiding
      // server-side-only changes (agent sends, delivery-status stamps) until an
      // unrelated Message mutation happened to invalidate it.
      const result = await messageStore.fetchList(
        {
          search: conversationSearch,
          pagination: { limit: 1000 },
          sort: { field: `createdDate`, direction: `ASC` },
        },
        { requestPolicy: `network-only` },
      )
      const response = result.data as any

      if (!response) return
      if (requestId !== latestLoadRequestIdRef.current) return

      if (result.error) {
        logError(`Error fetching messages`, result.error)
        setMessages([])
        setInboxContacts([])
        setMessagesCount(0)
        return
      }

      const nextMessages = (response?.messages?.hits ?? []) as MessageJson[]
      setMessages(nextMessages)
      setInboxContacts(response?.relatedContacts ?? [])
      setMessagesCount(response?.messages?.total ?? nextMessages.length)
      onAIThreadDetected?.(nextMessages.some((message) => message.aiWorkflowId))

      // `messageStore` excludes held rows, so a conversation whose only messages
      // are queued or cancelled comes back empty and leaves the thread unnamed.
      // The thread is real either way, and the agent lockout, the agent toggle
      // and thread naming all key off its id, so when nothing survived the
      // exclusion the id is read from a query that can still see those rows.
      // One extra request, and only in that case: any rendered message carries
      // the id already.
      if (nextMessages.some((message) => message.threadId)) {
        setHeldThreadId(undefined)
        return
      }
      const held = await gqlQuery(HELD_MESSAGES_QUERY, {
        search: conversationSearch,
        pagination: { limit: 1 },
        sort: { field: `createdDate`, direction: `ASC` },
      })
      if (requestId !== latestLoadRequestIdRef.current) return
      setHeldThreadId(held.data?.messages?.hits?.[0]?.threadId ?? undefined)
    },
    [contact?.id, contact?.locationId, contact?.cellPhone, onAIThreadDetected],
  )

  // The drawer can swap contacts in place, so nothing from the last one may render under this one.
  useEffect(() => {
    seenActivityIds.current = undefined
    setMessages([])
    setHeldThreadId(undefined)
    setInboxContacts([])
    setMessagesCount(0)
  }, [contact?.id])

  useEffect(() => {
    if (!contact?.id || !contact.locationId) {
      setMessages([])
      setHeldThreadId(undefined)
      setInboxContacts([])
      setMessagesCount(0)
      onAIThreadDetected?.(false)
      return
    }

    bg(loadMessages())
  }, [contact?.id, contact?.locationId, loadMessages, onAIThreadDetected])

  // Reload the drawer's messages when a Message changes on the live stream, so a text
  // lands here on the same edge that raises the alert card and refreshes the inbox list.
  useMessageEvents(
    () => {
      if (!contact?.id || !contact.locationId || pollFetchInFlightRef.current) return
      pollFetchInFlightRef.current = true
      bg(
        loadMessages().finally(() => {
          pollFetchInFlightRef.current = false
        }),
      )
    },
    !!(contact?.id && contact.locationId),
  )

  /**
   * Mark messages via the per-message mutation (the GraphQL surface has no bulk
   * mark-read), then reload canonical state and clear the updating flag — always.
   * The reload in `finally` doubles as the rollback: a failed mark leaves the
   * optimistic setMessages flip on screen otherwise, and a stuck
   * isUpdatingMessages would gate the message poll off for good (loadMessages
   * skips itself while the flag is up).
   */
  const runMarkMessages = async (targets: MessageJson[], read: boolean, failureMessage: string) => {
    try {
      const results = await Promise.all(
        targets.map((m) => gqlMutation(MARK_MESSAGE_READ_MUTATION, { id: m.id!, read })),
      )
      const firstError = results.find((r) => r.error)
      if (firstError?.error) {
        logError(failureMessage, firstError.error.message)
        notifyError(failureMessage)
      }
    } catch (err) {
      logError(failureMessage, err)
      notifyError(failureMessage)
    } finally {
      await loadMessages({ force: true }).catch((err: unknown) => logError(`Error reloading drawer messages`, err))
      setIsUpdatingMessages(false)
    }
  }

  const handleMarkAllRead = async (message: MessageJson) => {
    setIsUpdatingMessages(true)
    debug(`inbox`, `adding message`, message)
    const unreadIncoming = (messages ?? []).filter((m) => m.id && !m.read && !m.sentByUserId && !m.isAgent)
    const msgs = [...(messages ?? []), message].map((m) => ({ ...m, read: true }))
    setMessages(msgs)
    setMessagesCount(msgs.length)
    await runMarkMessages(unreadIncoming, true, `Error marking messages as read`)
  }

  // AI Agent toggle for the drawer's Inbox tab. Mirrors the right-panel toggle the
  // main inbox uses: when on, the workflow replies to inbound messages; off skips
  // them. State is derived from Thread.aiEnabled (defaults to "on" when thread exists).
  const opsContext = useMemo(() => ({ url: OPS_URL }), [])
  const [agentQuery] = useQuery<HasActiveAgentResult>({
    query: HAS_ACTIVE_AGENT_QUERY,
    variables: { locationId: contact?.locationId },
    pause: !contact?.locationId,
    context: opsContext,
  })
  const hasActiveAgent: boolean | undefined =
    !contact?.locationId || agentQuery.fetching || agentQuery.error
      ? agentQuery.error
        ? false
        : undefined
      : (agentQuery.data?.aiAgentConfigurations?.some((c) => !!c.systemPrompt?.trim()) ?? false)

  const activeThreadId = messages.find((m) => m.threadId)?.threadId ?? heldThreadId
  const [threadQuery] = useQuery<{ thread: { id: string; aiEnabled: boolean; name?: string | null } | null }>({
    query: THREAD_QUERY,
    variables: { id: activeThreadId },
    pause: !activeThreadId,
  })
  const [, updateThread] = useMutation(UPDATE_THREAD_MUTATION)
  const agentEnabled = threadQuery.data?.thread?.aiEnabled !== false
  // Strict on purpose: `agentEnabled` above is true while the thread query is in
  // flight, so gating the composer on it would blank it on every drawer open.
  const aiHandling = hasActiveAgent === true && threadQuery.data?.thread?.aiEnabled === true

  if (!me) return null

  const handleAgentToggle = async (next: boolean) => {
    if (!activeThreadId) return
    const result = await updateThread({ id: activeThreadId, input: { aiEnabled: next } })
    if (result.error) {
      notifyError(`Failed to update AI agent status`)
      logError(result.error.message)
    }
  }

  // Explicit Mark Read / Mark Unread for the drawer's Inbox tab. Mirrors
  // useInboxController.markThreadRead / markThreadUnread but operates against the
  // messages state owned by this component.
  const handleMarkThreadRead = async () => {
    const unreadIncoming = messages.filter((m) => m.id && !m.read && !m.sentByUserId && !m.isAgent)
    if (unreadIncoming.length === 0) return
    setIsUpdatingMessages(true)
    setMessages((prev) => prev.map((m) => (!m.read && !m.sentByUserId && !m.isAgent ? { ...m, read: true } : m)))
    await runMarkMessages(unreadIncoming, true, `Error marking messages as read`)
  }

  const handleMarkThreadUnread = async () => {
    const latestIncoming = [...messages].reverse().find((m) => m.id && !m.sentByUserId && !m.isAgent)
    if (!latestIncoming?.id) return
    setIsUpdatingMessages(true)
    setMessages((prev) => prev.map((m) => (m.id === latestIncoming.id ? { ...m, read: false } : m)))
    await runMarkMessages([latestIncoming], false, `Error marking message as unread`)
  }

  return (
    <div className="flex h-full flex-col">
      {/*
        Scrolled, the header rounds off its bottom corners and reads as a sheet
        the feed passes under. At rest it stays square against the content,
        because there is nothing underneath it to be above.
      */}
      <div
        className={classNames(
          `shrink-0 rounded-t-md border-b border-default bg-surface px-4 pt-4`,
          feedScrolled && `rounded-b-md`,
        )}
      >
        <InlineTabs
          tabs={tabs}
          initialTab={initialTab}
          className="!pb-[15px]"
          // Each tab has its own feed at its own top, so the header's shape has
          // to forget what the last one was doing.
          onTabChange={(next) => {
            setTab(next)
            setFeedScrolled(false)
          }}
          scrollable={!!detailsTab}
        />
        {tab === ActivitySections.LEDGER && ledgerChips.length > 0 && (
          <FilterChips
            chips={ledgerChips}
            active={visibleLedgerTypes}
            label="Transaction types"
            className={CHIP_ROW_PADDING}
            onToggle={(type) =>
              setHiddenLedgerTypes((current) => {
                const isHidden = current.includes(type)
                // Never hide the last one: an empty ledger says nothing.
                if (!isHidden && visibleLedgerTypes.length === 1) return current
                return isHidden ? current.filter((t) => t !== type) : [...current, type]
              })
            }
            onSelectAll={() => setHiddenLedgerTypes([])}
            onSelectOnly={(type) => setHiddenLedgerTypes(ledgerTypes.filter((other) => other !== type))}
          />
        )}
        {tab === ActivitySections.ALL && (
          <FilterChips
            chips={timelineChips}
            active={shownKinds}
            label="Timeline sources"
            className={CHIP_ROW_PADDING}
            onToggle={(kind) => {
              // The last lit chip stays on. toggleChipSelection cannot hold this
              // line itself: it sees one array at a time, and either can keep a
              // member that has no chip on this contact.
              if (litChips.length === 1 && litChips[0].kind === kind) return
              const isPmsKind = kind === PMS_APPOINTMENTS || kind === PMS_COMM_LOG
              // Both are written from what is on screen, so a selection that
              // fell back to the defaults is stored whole rather than half.
              setTimelineKinds(
                isPmsKind ? toggleChipSelection(shownTimelineKinds, kind as PmsRecordKind) : shownTimelineKinds,
              )
              setActivityKinds(
                isPmsKind ? shownActivityKinds : toggleChipSelection(shownActivityKinds, kind as ActivityKind),
              )
            }}
            onSelectAll={() => {
              setActivityKinds(activityChips.map((chip) => chip.kind))
              setTimelineKinds([RECORD_ACTIVITIES, PMS_APPOINTMENTS, PMS_COMM_LOG])
            }}
            onSelectOnly={(kind) => {
              if (kind === PMS_APPOINTMENTS || kind === PMS_COMM_LOG) {
                setTimelineKinds([RECORD_ACTIVITIES, kind])
                setActivityKinds([])
              } else {
                setTimelineKinds([RECORD_ACTIVITIES])
                setActivityKinds([kind as ActivityKind])
              }
            }}
          />
        )}
      </div>
      <div className="mb-1 flex grow flex-col space-y-2 overflow-hidden rounded-b-md bg-surface">
        {tab === DETAILS_TAB && detailsTab ? (
          <div className="h-full overflow-y-auto">{detailsTab.content}</div>
        ) : tab === ActivitySections.DOCUMENTS ? (
          <DocumentsTab {...documents} />
        ) : !activities ? (
          <div className="mt-3 flex h-full w-full items-center justify-center align-middle">
            <Spinner size={20} />
          </div>
        ) : (
          <ActivityPanel
            contact={contact}
            type={tab as ActivitySections}
            scrolled={feedScrolled}
            onScrolledChange={setFeedScrolled}
            activities={activities}
            messages={messages}
            inboxContacts={inboxContacts}
            appointments={appointments}
            transactions={visibleTransactions}
            treatmentPlans={treatmentPlans}
            treatmentWork={{
              procedures: treatmentWork.procedures,
              plans: treatmentWork.plans,
              fetching: treatmentWork.fetching,
              hasError: !!treatmentWork.error,
              hasPatientLink: treatmentWork.hasPatientLink,
            }}
            activityKinds={shownActivityKinds}
            pmsKinds={shownTimelineKinds}
            commLog={{
              entries: commLogFeed.entries,
              fetching: commLogFeed.fetching,
              hasError: !!commLogFeed.error,
              hasMore: commLogFeed.hasMore,
              hasPatientLink: commLogFeed.hasPatientLink,
              loadOlder: commLogFeed.loadOlder,
              retry: commLogFeed.retry,
            }}
            onActivityAdded={async (activity) => {
              if (!activities) return
              let activityIndex = activities.findIndex(({ id }) => id! === activity.id!)
              if (activityIndex >= 0) {
                const updated = [...activities]
                updated[activityIndex] = activity
                onActivitiesChange?.(updated)
              } else {
                onActivitiesChange?.([activity, ...(activities ?? [])])
              }
              if (activity.type === `FollowUp` && contact) {
                // check if the drawer is open for the selected contact and if so, update the contact
                const urlDrawerParam = new URL(window.location.href).searchParams.get(`drawer`)
                if (urlDrawerParam === contact.id) {
                  onChange?.(contact)
                }
              }
            }}
            onActivityRemoved={(activityId) => {
              if (!activities) return
              let removedActivity = activities?.find((a) => a.id === activityId)
              if (removedActivity?.type === `FollowUp` && contact) onChange?.(contact)
              if (workspace?.id && removedActivity?.type === `Task` && contact) onRemovedTask?.()
              onActivitiesChange?.(activities?.filter((a) => a.id !== activityId) || [])
            }}
            onActivityUpdated={(activity) => {
              if (!activities) return
              let activityIndex = activities.findIndex(({ id }) => id! === activity.id!)
              if (activityIndex >= 0) {
                const updated = [...activities]
                updated[activityIndex] = activity
                onActivitiesChange?.(updated)
              }
              contact && onChange?.(contact)
            }}
            onTaskUpdated={onTaskUpdated}
            onMessage={handleMarkAllRead}
            onMarkAllRead={handleMarkThreadRead}
            onMarkUnread={handleMarkThreadUnread}
            agentEnabled={agentEnabled}
            aiHandling={aiHandling}
            hasActiveAgent={hasActiveAgent}
            onAgentToggle={handleAgentToggle}
            onContactRefresh={() => contact && onChange?.(contact)}
            highlightActivityId={highlightActivityId}
          />
        )}
      </div>
    </div>
  )
})
